import { ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatCurrency, toNumber } from "@/lib/formatters";
import { cn } from "@/lib/utils";

export const SCHEDULED_VALUE_TEXT_PRESETS = ["Incl. Above", "By Owner", "Excluded"] as const;

interface ScheduledValueCellProps {
  numericValue: string;
  textValue: string | null;
  disabled?: boolean;
  onCommit: (next: { numeric: string; text: string | null }) => void;
}

/**
 * Scheduled Value cell — accepts either a dollar amount or a descriptive
 * text token like "Incl. Above" / "By Owner" / "Excluded". Text-value rows
 * carry $0 in numeric totals but preserve intent per GMP practice (AC#9).
 */
export function ScheduledValueCell({
  numericValue,
  textValue,
  disabled,
  onCommit,
}: ScheduledValueCellProps) {
  const isText = !!textValue;
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState(numericValue);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!focused) setDraft(numericValue);
  }, [numericValue, focused]);

  function commitNumeric() {
    const trimmed = draft.trim();
    const normalized = trimmed === "" ? "0" : toNumber(trimmed).toFixed(2).replace(/\.00$/, "");
    if (toNumber(normalized) !== toNumber(numericValue) || textValue !== null) {
      onCommit({ numeric: normalized, text: null });
    }
  }

  function choosePreset(preset: string) {
    if (preset === textValue && toNumber(numericValue) === 0) return;
    onCommit({ numeric: "0", text: preset });
  }

  if (disabled) {
    return (
      <span className="text-sm text-gray-900">
        {isText ? textValue : formatCurrency(numericValue)}
      </span>
    );
  }

  return (
    <div className="flex items-center justify-end gap-1">
      {isText ? (
        <span className="inline-flex items-center rounded bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700">
          {textValue}
        </span>
      ) : (
        <input
          ref={inputRef}
          type="text"
          inputMode="decimal"
          aria-label="Scheduled value"
          value={focused ? draft : formatDisplay(numericValue)}
          onFocus={(e) => {
            setFocused(true);
            setDraft(stripFormatting(numericValue));
            e.target.select();
          }}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            setFocused(false);
            commitNumeric();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              inputRef.current?.blur();
            } else if (e.key === "Escape") {
              setDraft(numericValue);
              inputRef.current?.blur();
            }
          }}
          className={cn(
            "h-8 w-28 rounded border border-transparent bg-transparent px-2 text-right text-sm",
            "hover:border-gray-300",
            "focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900 focus:bg-white",
          )}
        />
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-gray-400 hover:text-gray-700"
            aria-label="Scheduled value presets"
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {SCHEDULED_VALUE_TEXT_PRESETS.map((preset) => (
            <DropdownMenuItem key={preset} onSelect={() => choosePreset(preset)}>
              {preset}
            </DropdownMenuItem>
          ))}
          {isText ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => onCommit({ numeric: "0", text: null })}>
                Clear — enter amount
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function stripFormatting(value: string): string {
  const n = toNumber(value);
  if (n === 0) return "";
  const s = n.toFixed(2);
  return s.endsWith(".00") ? s.slice(0, -3) : s;
}

function formatDisplay(value: string): string {
  const n = toNumber(value);
  return formatCurrency(n, { precise: true });
}
